import React, { useState, useEffect, useCallback } from 'react';
import { searchFoods, filterFoods, getCategories } from '../../services/foodService';
import { useDebounce } from 'use-debounce';
import { toast } from 'react-hot-toast';
import MenuBrowser from './MenuBrowser';
import MealBucket from './MealBucket';
import './Step3ShoppingCart.css';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snacks'];

const Step3ShoppingCart = ({ targets, dailyTargets, cart, mealsPerDay, isCoachMode, affiliateId }) => {
  // Source toggle state
  const [activeSource, setActiveSource] = useState('menus'); // 'menus' or 'foods'
  
  // Search state for foods
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebounce(searchQuery, 300);
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [foodTypeFilter, setFoodTypeFilter] = useState('all'); // 'all', 'branded', 'unbranded'
  
  // Meal type for adding foods
  const [selectedMealType, setSelectedMealType] = useState('snacks');

  // Get current day info from cart
  const currentDayInfo = cart.weekPlan?.find(d => d.dayNumber === cart.currentDay);
  const weekSummary = cart.getWeekSummary;

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data.categories || []);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Search/filter foods
  const fetchFoods = useCallback(async () => {
    if (activeSource !== 'foods') return;
    
    setLoading(true);
    try {
      let result;
      const dataTypeParam = foodTypeFilter !== 'all' ? foodTypeFilter : undefined;
      
      if (debouncedQuery.length >= 2) {
        result = await searchFoods(debouncedQuery, {
          category: selectedCategory || undefined,
          dataType: dataTypeParam,
          limit: 30
        });
        setFoods(result.foods || []);
      } else if (selectedCategory) {
        result = await filterFoods({
          category: selectedCategory,
          dataType: dataTypeParam
        }, { limit: 30 });
        setFoods(result.foods || []);
      } else {
        setFoods([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Failed to search foods');
    } finally {
      setLoading(false);
    }
  }, [activeSource, debouncedQuery, selectedCategory, foodTypeFilter]);

  useEffect(() => {
    fetchFoods();
  }, [fetchFoods]);

  const handleAddFood = (food) => {
    cart.addFood(food, selectedMealType, 1, 100);
    toast.success(`Added ${food.description} to ${selectedMealType}`, { duration: 1500 });
  };

  const handleAddMenuItem = (menuItem, mealType) => {
    cart.addMenuItem(menuItem, mealType, 1);
  };

  const dailyProgress = cart.calculateDailyProgress(dailyTargets);
  const weekProgress = cart.calculateWeekProgress ? cart.calculateWeekProgress(dailyTargets) : null;

  const formatNutrient = (value, unit = 'g') => {
    if (value === undefined || value === null) return '-';
    return `${Math.round(value * 10) / 10}${unit}`;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format the start date for display
  const formatWeekRange = () => {
    if (!cart.startDate || !cart.weekPlan?.length) return '';
    const startDate = new Date(cart.startDate + 'T00:00:00');
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  return (
    <div className="step3-shopping-cart">
      <div className="step-intro">
        <h2><i className="fas fa-calendar-alt"></i> Build Your Week Meal Plan</h2>
        <p>Plan your meals for the week. Navigate between days to build a complete meal plan.</p>
      </div>

      {/* Week Date Picker */}
      <div className="week-date-picker">
        <label><i className="fas fa-calendar-week"></i> Week Starting:</label>
        <input 
          type="date" 
          value={cart.startDate || new Date().toISOString().split('T')[0]}
          onChange={(e) => cart.setWeekStartDate(e.target.value)}
          className="date-input"
        />
        <span className="week-range">{formatWeekRange()}</span>
      </div>

      {/* Week Navigation */}
      <div className="week-navigation">
        <div className="day-nav-controls">
          <button 
            className="day-nav-btn"
            onClick={cart.previousDay}
            disabled={cart.currentDay <= 1}
          >
            <i className="fas fa-chevron-left"></i>
          </button>
          
          <div className="current-day-info">
            <span className="day-number">Day {cart.currentDay} of {cart.WEEK_DAYS}</span>
            <span className="day-name">
              {currentDayInfo?.dayName} {formatDate(currentDayInfo?.date)}
            </span>
          </div>
          
          <button 
            className="day-nav-btn"
            onClick={cart.nextDay}
            disabled={cart.currentDay >= cart.WEEK_DAYS}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        {/* Week Overview */}
        <div className="week-overview">
          {weekSummary?.map(day => (
            <button
              key={day.dayNumber}
              className={`day-dot ${day.dayNumber === cart.currentDay ? 'active' : ''} ${day.hasItems ? 'has-items' : ''}`}
              onClick={() => cart.setCurrentDay(day.dayNumber)}
              title={`${day.dayName}: ${day.itemCount} items`}
            >
              <span className="dot-day">{day.dayName.substring(0, 1)}</span>
              {day.hasItems && <span className="dot-indicator"></span>}
            </button>
          ))}
        </div>

        {/* Week Progress Bar */}
        <div className="week-progress-bar">
          <div className="progress-label">
            <span>Week Progress</span>
            <span>{cart.getDaysWithItems} of {cart.WEEK_DAYS} days planned</span>
          </div>
          <div className="progress-track">
            <div 
              className="progress-fill"
              style={{ width: `${(cart.getDaysWithItems / cart.WEEK_DAYS) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Daily Targets Banner */}
      {dailyTargets && (
        <div className="daily-targets-banner">
          <span className="targets-label">Daily Targets:</span>
          <span className="target-pill calories">{dailyTargets.dailyCalories} cal</span>
          <span className="target-pill protein">{dailyTargets.dailyProtein}g protein</span>
          <span className="target-pill carbs">{dailyTargets.dailyCarbs}g carbs</span>
          <span className="target-pill fat">{dailyTargets.dailyFat}g fat</span>
        </div>
      )}

      <div className="cart-layout-v2">
        {/* Left Panel: Source Selection */}
        <div className="source-panel">
          {/* Source Toggle */}
          <div className="source-toggle">
            <button 
              className={`toggle-btn ${activeSource === 'menus' ? 'active' : ''}`}
              onClick={() => setActiveSource('menus')}
            >
              <i className="fas fa-book-open"></i>
              Recipes
            </button>
            <button 
              className={`toggle-btn ${activeSource === 'foods' ? 'active' : ''}`}
              onClick={() => setActiveSource('foods')}
            >
              <i className="fas fa-leaf"></i>
              Individual Foods
            </button>
          </div>

          {/* Menu Browser */}
          {activeSource === 'menus' && (
            <MenuBrowser 
              onAddMenuItem={handleAddMenuItem}
              isMenuItemInPlan={cart.isMenuItemInPlan}
              isCoachMode={isCoachMode}
              affiliateId={affiliateId}
              canAddMenuItem={cart.canAddMenuItem}
              menuSource={cart.menuSource}
              onClearPlan={cart.clearPlan}
            />
          )}

          {/* Food Search */}
          {activeSource === 'foods' && (
            <div className="food-search-section">
              {/* Meal Type Selector for Foods */}
              <div className="meal-type-selector">
                <span className="selector-label">Add to:</span>
                {MEAL_TYPES.map(type => (
                  <button
                    key={type}
                    className={`meal-type-btn ${selectedMealType === type ? 'active' : ''}`}
                    onClick={() => setSelectedMealType(type)}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>

              {/* Search Controls */}
              <div className="search-controls">
                <div className="search-input-wrapper">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    placeholder="Search foods (e.g., granola, beef jerky...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button className="clear-btn" onClick={() => setSearchQuery('')}>
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>

                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-select"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.name} value={cat.name}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Food Type Filter */}
              <div className="food-type-filter">
                <span className="filter-label">Show:</span>
                <div className="filter-toggle">
                  <button
                    className={`filter-btn ${foodTypeFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setFoodTypeFilter('all')}
                  >
                    All Foods
                  </button>
                  <button
                    className={`filter-btn ${foodTypeFilter === 'unbranded' ? 'active' : ''}`}
                    onClick={() => setFoodTypeFilter('unbranded')}
                    title="Generic foods like 'Apple' or 'Chicken breast'"
                  >
                    <i className="fas fa-apple-alt"></i> Generic
                  </button>
                  <button
                    className={`filter-btn ${foodTypeFilter === 'branded' ? 'active' : ''}`}
                    onClick={() => setFoodTypeFilter('branded')}
                    title="Brand-name products like 'Cheerios' or 'KIND bars'"
                  >
                    <i className="fas fa-tag"></i> Branded
                  </button>
                </div>
              </div>

              {/* Food Results */}
              <div className="food-results">
                {loading ? (
                  <div className="loading-state">
                    <i className="fas fa-spinner fa-spin"></i> Searching...
                  </div>
                ) : foods.length > 0 ? (
                  <div className="food-list">
                    {foods.map(food => (
                      <div key={food.fdcId} className="food-item">
                        <div className="food-info">
                          <h4>{food.description}</h4>
                          <span className="food-category">
                            {food.category}
                            {food.brandOwner && ` • ${food.brandOwner}`}
                          </span>
                        </div>
                        <div className="food-macros">
                          <span className="macro protein">{formatNutrient(food.nutrients?.protein)} P</span>
                          <span className="macro calories">{formatNutrient(food.nutrients?.calories, '')} cal</span>
                        </div>
                        {food.matchScore !== undefined && (
                          <div className="match-score">{food.matchScore}% match</div>
                        )}
                        <button
                          className={`add-btn ${cart.isFoodInPlan(food.fdcId) ? 'in-cart' : ''}`}
                          onClick={() => handleAddFood(food)}
                        >
                          <i className="fas fa-plus"></i>
                          {cart.isFoodInPlan(food.fdcId) ? 'Add More' : 'Add'}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <i className="fas fa-search"></i>
                    <p>Search for foods or use "Find Matches"</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel: Meal Plan & Progress */}
        <div className="step3-plan-panel">
          {/* Daily Progress */}
          {dailyTargets && dailyProgress && (
            <div className="step3-daily-progress">
              <h3><i className="fas fa-chart-pie"></i> Day {cart.currentDay} Progress</h3>
              <div className="step3-progress-grid">
                <div className="step3-progress-item">
                  <div className="step3-circle-container">
                    <svg className="step3-circle" viewBox="0 0 36 36">
                      <path 
                        className="step3-circle-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path 
                        className="step3-circle-fill calories"
                        strokeDasharray={`${Math.min(dailyProgress.calories, 100)}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="step3-percent">{dailyProgress.calories}%</span>
                  </div>
                  <span className="step3-macro-label">Calories</span>
                  <span className="step3-values">{cart.dailyTotals.calories} / {dailyTargets.dailyCalories}</span>
                </div>
                <div className="step3-progress-item">
                  <div className="step3-circle-container">
                    <svg className="step3-circle" viewBox="0 0 36 36">
                      <path 
                        className="step3-circle-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path 
                        className="step3-circle-fill protein"
                        strokeDasharray={`${Math.min(dailyProgress.protein, 100)}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="step3-percent">{dailyProgress.protein}%</span>
                  </div>
                  <span className="step3-macro-label">Protein</span>
                  <span className="step3-values">{cart.dailyTotals.protein}g / {dailyTargets.dailyProtein}g</span>
                </div>
                <div className="step3-progress-item">
                  <div className="step3-circle-container">
                    <svg className="step3-circle" viewBox="0 0 36 36">
                      <path 
                        className="step3-circle-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path 
                        className="step3-circle-fill carbs"
                        strokeDasharray={`${Math.min(dailyProgress.carbs, 100)}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="step3-percent">{dailyProgress.carbs}%</span>
                  </div>
                  <span className="step3-macro-label">Carbs</span>
                  <span className="step3-values">{cart.dailyTotals.carbs}g / {dailyTargets.dailyCarbs}g</span>
                </div>
                <div className="step3-progress-item">
                  <div className="step3-circle-container">
                    <svg className="step3-circle" viewBox="0 0 36 36">
                      <path 
                        className="step3-circle-bg"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path 
                        className="step3-circle-fill fat"
                        strokeDasharray={`${Math.min(dailyProgress.fat, 100)}, 100`}
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="step3-percent">{dailyProgress.fat}%</span>
                  </div>
                  <span className="step3-macro-label">Fat</span>
                  <span className="step3-values">{cart.dailyTotals.fat}g / {dailyTargets.dailyFat}g</span>
                </div>
              </div>
            </div>
          )}

          {/* Meal Buckets */}
          <div className="meal-buckets">
            <div className="buckets-header">
              <h3>
                <i className="fas fa-utensils"></i> 
                {currentDayInfo?.dayName}'s Meals
              </h3>
              <span className="total-items">{cart.totalItemCount} items</span>
            </div>
            
            <div className="buckets-grid">
              {MEAL_TYPES.map(mealType => (
                <MealBucket
                  key={mealType}
                  mealType={mealType}
                  foods={cart.mealPlan[mealType].foods}
                  menuItems={cart.mealPlan[mealType].menuItems}
                  totals={cart.getMealTotals(mealType)}
                  onRemoveFood={cart.removeFood}
                  onRemoveMenuItem={cart.removeMenuItem}
                  onUpdateFoodQuantity={cart.updateFoodQuantity}
                  onUpdateMenuItemServings={cart.updateMenuItemServings}
                  onClearMeal={cart.clearMeal}
                />
              ))}
            </div>

            {cart.totalItemCount > 0 && (
              <div className="plan-actions">
                <button className="clear-day-btn" onClick={() => cart.clearDay(cart.currentDay)}>
                  <i className="fas fa-eraser"></i> Clear Day
                </button>
                <button className="clear-plan-btn" onClick={cart.clearPlan}>
                  <i className="fas fa-trash"></i> Clear Week
                </button>
              </div>
            )}
          </div>

          {/* Week Summary Card */}
          {cart.weekItemCount > 0 && (
            <div className="week-summary-card">
              <h4><i className="fas fa-calendar-week"></i> Week Summary</h4>
              <div className="week-stats">
                <div className="stat">
                  <span className="stat-value">{cart.weekItemCount}</span>
                  <span className="stat-label">Total Items</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{cart.getWeekTotals?.calories || 0}</span>
                  <span className="stat-label">Total Calories</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{cart.getWeekTotals?.protein || 0}g</span>
                  <span className="stat-label">Total Protein</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step3ShoppingCart;
